// src/features/equipment/hooks/useEquipmentTemplates.js
import { useState, useCallback, useMemo } from 'react';
import { equipmentTemplatesAPI } from '../services/equipmentTemplates';
import { useApi } from '../../../hooks/api/useApi';

export function useEquipmentTemplates() {
  const [templates, setTemplates] = useState([]);
  const { loading, error, execute, clearError } = useApi();

  const fetchTemplates = useCallback(
    async (params = {}) => {
      const result = await execute(() => equipmentTemplatesAPI.list(params));
      if (result.success) {
        setTemplates(result.data.templates || []);
      }
      return result;
    },
    [execute]
  );

  const createTemplate = useCallback(
    async (data) => execute(() => equipmentTemplatesAPI.create(data)),
    [execute]
  );

  const updateTemplate = useCallback(
    async (templateId, data) =>
      execute(() => equipmentTemplatesAPI.update(templateId, data)),
    [execute]
  );

  const deleteTemplate = useCallback(
    async (templateId) => execute(() => equipmentTemplatesAPI.delete(templateId)),
    [execute]
  );

  const linkTaskTemplate = useCallback(
    async (templateId, taskTemplateId) =>
      execute(() => equipmentTemplatesAPI.linkTaskTemplate(templateId, taskTemplateId)),
    [execute]
  );

  const unlinkTaskTemplate = useCallback(
    async (templateId, taskTemplateId) =>
      execute(() => equipmentTemplatesAPI.unlinkTaskTemplate(templateId, taskTemplateId)),
    [execute]
  );

  // Grouped by equipment_type, preserving server sort order within each group
  const templatesByType = useMemo(() => {
    return templates.reduce((acc, t) => {
      if (!acc[t.equipment_type]) acc[t.equipment_type] = [];
      acc[t.equipment_type].push(t);
      return acc;
    }, {});
  }, [templates]);

  const equipmentTypes = useMemo(
    () => [...new Set(templates.map((t) => t.equipment_type))],
    [templates]
  );

  const manufacturers = useMemo(
    () => [...new Set(templates.map((t) => t.manufacturer).filter(Boolean))].sort(),
    [templates]
  );

  return {
    templates,
    templatesByType,
    equipmentTypes,
    manufacturers,
    loading,
    error,
    clearError,
    fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    linkTaskTemplate,
    unlinkTaskTemplate,
  };
}
